import { useEffect, useReducer, createContext, useContext } from "react";
import { goalsService } from "../services/goalsService";
import { useToast } from "./useToast";
import { NOTIFICATION_TYPES, formatCurrency } from "../utils";
import { useAuth } from "./useAuth";
import { useCurrency } from "./useCurrency";

// Create a context to share goals state across all components
const GoalsContext = createContext();

// Use a reducer instead of setState to ensure proper updates
const goalsReducer = (state, action) => {
  switch (action.type) {
    case "SET_GOALS":
      return { ...state, goals: [...action.payload] };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "ADD_GOAL":
      const withNewGoal = [...state.goals, action.payload];
      return { ...state, goals: withNewGoal };
    case "UPDATE_GOAL":
      const withUpdatedGoal = state.goals.map((goal) =>
        goal.id === action.payload.id ? action.payload : goal,
      );
      return { ...state, goals: withUpdatedGoal };
    case "DELETE_GOAL":
      const withDeletedGoal = state.goals.filter(
        (goal) => goal.id !== action.payload,
      );
      return { ...state, goals: withDeletedGoal };
    default:
      return state;
  }
};

// Provider component to wrap the app
export const GoalsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(goalsReducer, {
    goals: [],
    loading: true,
    error: null,
  });

  const { currency: defaultCurrency } = useCurrency();

  const addToast = useToast();
  const { isLoggedIn, isCheckingAuth } = useAuth(); // Add this line

  // Helper function to parse API response structure
  const parseGoalsResponse = (response) => {
    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.results)) {
      return response.results;
    } else if (response && Array.isArray(response.data)) {
      return response.data;
    } else if (response && typeof response === "object") {
      return [response];
    } else {
      console.warn("Unexpected API response structure:", response);
      return [];
    }
  };

  // Generic helper for optimistic updates with server sync
  const performGoalOperation = async (
    operation,
    optimisticAction,
    successMessage,
    errorMessage,
    { silent = false } = {},
  ) => {
    try {
      // Perform the API operation
      const result = await operation();

      // Apply optimistic update immediately
      dispatch(optimisticAction(result));

      // Show success toast (unless silent — caller handles batch toast)
      if (!silent) {
        addToast({
          content: successMessage,
          type: NOTIFICATION_TYPES.success,
        });
      }

      // Sync with server as backup
      setTimeout(async () => {
        try {
          const freshGoals = await goalsService.getGoals();
          const parsedGoals = parseGoalsResponse(freshGoals);
          dispatch({ type: "SET_GOALS", payload: parsedGoals });
        } catch (syncError) {
          console.error(
            "Server sync failed, keeping optimistic update:",
            syncError,
          );
        }
      }, 200);

      return result;
    } catch (err) {
      console.error("Goal operation failed:", err);
      addToast({
        content: errorMessage,
        type: NOTIFICATION_TYPES.error,
      });
      throw err;
    }
  };

  // Create a new goal
  const createGoal = async (goalData, options) => {
    return performGoalOperation(
      () => goalsService.createGoal(goalData),
      (newGoal) => ({ type: "ADD_GOAL", payload: newGoal }),
      "Goal created successfully",
      "Failed to create goal",
      options,
    );
  };

  // Update an existing goal
  const updateGoal = async (goalId, goalData, options) => {
    return performGoalOperation(
      () => goalsService.updateGoal(goalId, goalData),
      (updatedGoal) => ({ type: "UPDATE_GOAL", payload: updatedGoal }),
      "Goal updated successfully",
      "Failed to update goal",
      options,
    );
  };

  // Delete a goal
  const deleteGoal = async (goalId) => {
    return performGoalOperation(
      () => goalsService.deleteGoal(goalId),
      () => ({ type: "DELETE_GOAL", payload: goalId }),
      "Goal deleted successfully",
      "Failed to delete goal",
    );
  };

  // Manual refresh function for external use (with toast)
  const fetchGoals = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });
      const data = await goalsService.getGoals();
      const goalsArray = parseGoalsResponse(data);
      dispatch({ type: "SET_GOALS", payload: goalsArray });
      dispatch({ type: "SET_LOADING", payload: false });
    } catch (err) {
      console.error("Error in fetchGoals:", err);
      dispatch({ type: "SET_ERROR", payload: err.message });
      dispatch({ type: "SET_GOALS", payload: [] });
      dispatch({ type: "SET_LOADING", payload: false });
      addToast({
        content: "Failed to load goals",
        type: NOTIFICATION_TYPES.error,
      });
    }
  };

  // Get goals by type
  const getGoalsByType = (goalType) => {
    return Array.isArray(state.goals)
      ? state.goals.filter((goal) => goal.goal_type === goalType)
      : [];
  };

  // Get upcoming goals (due within next 30 days)
  const getUpcomingGoals = () => {
    if (!Array.isArray(state.goals)) return [];

    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return state.goals.filter((goal) => {
      const dueDate = new Date(goal.due_date);
      return dueDate <= thirtyDaysFromNow && dueDate >= new Date();
    });
  };

  // Get overdue goals
  const getOverdueGoals = () => {
    if (!Array.isArray(state.goals)) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return state.goals.filter((goal) => {
      const dueDate = new Date(goal.due_date);
      return dueDate < today;
    });
  };

  // Calculate days until due date
  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    // Fix timezone issue by treating the date as local time
    return new Date(dateString + "T00:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatNumber = (number) => {
    if (!number && number !== 0) return "0";
    // Convert string to number if needed
    const numValue = typeof number === "string" ? parseFloat(number) : number;
    if (isNaN(numValue)) return "0";

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  const getGoalTargetValue = (goal) => {
    if (goal.goal_category === "monetary") {
      return formatCurrency(
        goal.monetary_target,
        goal.monetary_target_currency || defaultCurrency,
        { abbreviated: false }, // Goals show full values, not K/M abbreviated
      );
    }

    return formatNumber(goal.numerical_target);
  };

  const isGoalAchieved = (goal) => {
    return goal.is_achieved || false;
  };

  const getGoalProgress = (goal) => {
    return Math.round(goal.progress_percentage || 0);
  };

  // Fetch goals on mount (without toast for initial load) - ONLY when logged in
  useEffect(() => {
    // Only fetch goals if user is logged in and not checking auth
    if (!isLoggedIn || isCheckingAuth) {
      dispatch({ type: "SET_LOADING", payload: false });
      return;
    }

    const loadGoals = async () => {
      try {
        dispatch({ type: "SET_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: null });
        const data = await goalsService.getGoals();
        const goalsArray = parseGoalsResponse(data);
        dispatch({ type: "SET_GOALS", payload: goalsArray });
      } catch (err) {
        console.error("Error in initial load:", err);

        // Don't show error for auth errors (401) as they'll redirect to login
        if (err.response?.status !== 401) {
          dispatch({ type: "SET_ERROR", payload: err.message });
        }
        dispatch({ type: "SET_GOALS", payload: [] });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadGoals();
  }, [isLoggedIn, isCheckingAuth]); // Add dependencies

  const contextValue = {
    goals: Array.isArray(state.goals) ? state.goals : [],
    loading: state.loading,
    error: state.error,
    fetchGoals,
    createGoal,
    updateGoal,
    deleteGoal,
    getGoalsByType,
    getUpcomingGoals,
    getOverdueGoals,
    getDaysUntilDue,
    formatDate,
    getGoalTargetValue,
    isGoalAchieved,
    getGoalProgress,
  };

  return (
    <GoalsContext.Provider value={contextValue}>
      {children}
    </GoalsContext.Provider>
  );
};

// Hook to consume the context
export const useGoals = () => {
  const context = useContext(GoalsContext);
  if (!context) {
    throw new Error("useGoals must be used within a GoalsProvider");
  }

  return context;
};
